import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import MultiSelectDropdown from '../../../components/MultiSelectDropdown/MultiSelectDropdown';
import { SessionFilterType, SessionFilterTypeOption } from './types';
import useSessionContext from './useSessionContext';

export type SessionFilterProps = {
  label?: string;
  labelFilter?: boolean;
  labelClassName?: string;
  filterClassName?: string;
  filterType?: SessionFilterType[];
  showClearFilter?: boolean;
  displayAllTypes?: boolean;
  filtersOccupyWidth?: boolean;
  hideFiltersOnDefault?: boolean;
};

const SessionFilter = ({
  label = 'Filter by',
  labelFilter = true,
  labelClassName,
  filterClassName,
  filterType = [SessionFilterType.TYPE, SessionFilterType.STUDENT, SessionFilterType.SUBJECT],
  showClearFilter = true,
  displayAllTypes = false,
  filtersOccupyWidth = true,
  hideFiltersOnDefault = true,
}: SessionFilterProps) => {
  const {
    filters: {
      selectedFilters,
      setSelectedFilters,
      selectedType,
      setSelectedType,
      selectedStudent,
      setSelectedStudent,
      studentOptions,
      selectedSubject,
      setSelectedSubject,
      subjectOptions,
    },
    sessionTypes,
  } = useSessionContext();
  const typeOptions = useMemo(
    () => (displayAllTypes ? SessionFilterTypeOption : sessionTypes),
    [displayAllTypes, sessionTypes]
  );

  useEffect(() => {
    if (!hideFiltersOnDefault) {
      if (Object.entries(sessionTypes).length > 1) setSelectedFilters(filterType);
      else setSelectedFilters(filterType.filter((filter) => filter !== SessionFilterType.TYPE));
    }
    setSelectedType(Object.values(typeOptions));
    setSelectedSubject(Object.values(subjectOptions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionTypes, subjectOptions, typeOptions]);

  const handleClearFilterType = (removeFilter: SessionFilterType) => {
    setSelectedFilters(
      selectedFilters.includes(removeFilter)
        ? selectedFilters.filter((filter) => filter !== removeFilter)
        : selectedFilters
    );
  };

  return (
    <div
      className={classNames('flex gap-x-3 lg:flex-row', filterClassName, filtersOccupyWidth ? 'flex-col' : 'flex-row')}
    >
      <div className={classNames('content-center lg:mb-0', labelFilter && 'mb-4')}>
        {labelFilter ? (
          <>
            <MultiSelectDropdown
              options={Object.fromEntries(
                (Object.entries(sessionTypes).length > 1
                  ? filterType
                  : filterType.filter((filter) => filter !== SessionFilterType.TYPE)
                ).map((filter) => [filter, filter])
              )}
              selectedOptions={selectedFilters}
              placeholder={label}
              onChange={setSelectedFilters}
              reflectParentOptionsState={true}
              showSelected={false}
              showClearIcon={false}
              buttonClassName="!w-auto !border-none !pl-0 !pr-3"
              labelClassName={`text-base ${labelClassName}`}
              dropdownClassName="!w-auto !min-w-[120px] !p-3"
            />
          </>
        ) : (
          <span className={classNames('text-base', labelClassName)}>{label}</span>
        )}
      </div>
      <div
        className={classNames(
          'grid gap-x-2 gap-y-2 lg:flex lg:flex-row',
          filtersOccupyWidth ? 'grid-cols-2' : 'grid-cols-1'
        )}
      >
        <div
          className={classNames('mr-2', selectedFilters.includes(SessionFilterType.TYPE) ? 'inline-block' : 'hidden')}
        >
          <MultiSelectDropdown
            options={typeOptions}
            selectedOptions={selectedType}
            placeholder="Type"
            onChange={setSelectedType}
            buttonClassName="!w-auto rounded-[32px] !border-none bg-[#E7E9E9]"
            labelClassName="text-base"
            selectedClassName="text-base"
            dropdownClassName="!w-auto !min-w-[120px] !p-3"
            onClear={() => handleClearFilterType(SessionFilterType.TYPE)}
            keyAsLabel={true}
            showClearIcon={showClearFilter}
          />
        </div>
        <div
          className={classNames(
            'mr-2',
            selectedFilters.includes(SessionFilterType.STUDENT) ? 'inline-block' : 'hidden'
          )}
        >
          <MultiSelectDropdown
            options={Object.fromEntries(studentOptions.map((student) => [student, student]))}
            selectedOptions={selectedStudent}
            placeholder="Student"
            onChange={setSelectedStudent}
            buttonClassName="!w-auto rounded-[32px] !border-none bg-[#E7E9E9]"
            labelClassName="text-base"
            selectedClassName="text-base"
            dropdownClassName="!w-[200px] !p-3"
            onClear={() => handleClearFilterType(SessionFilterType.STUDENT)}
            showClearIcon={showClearFilter}
          />
        </div>
        <div
          className={classNames(
            'mr-2',
            selectedFilters.includes(SessionFilterType.SUBJECT) ? 'inline-block' : 'hidden'
          )}
        >
          <MultiSelectDropdown
            options={Object.fromEntries(subjectOptions.map((subject) => [subject, subject]))}
            selectedOptions={selectedSubject}
            placeholder="Subject"
            onChange={setSelectedSubject}
            buttonClassName="!w-auto rounded-[32px] !border-none bg-[#E7E9E9]"
            labelClassName="text-base"
            selectedClassName="text-base"
            dropdownClassName="!w-[200px] !p-3"
            onClear={() => handleClearFilterType(SessionFilterType.SUBJECT)}
            showClearIcon={showClearFilter}
          />
        </div>
      </div>
    </div>
  );
};

export default SessionFilter;
